/* Pipeline provider configuration card (split from CDMDocumentsSettings.jsx) */
import React from 'react';
import { PIPELINE_API, authedFetch } from './api';
import { ProviderHealthPanel } from './ProviderHealthPanel';

export function PipelineConfigCard({ onClose }) {
  const [settings, setSettings] = React.useState(null);
  const [availableProviders, setAvailableProviders] = React.useState({});
  const [subservices, setSubservices] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState(null);
  const [cacheCleared, setCacheCleared] = React.useState(false);
  const [embStatus, setEmbStatus] = React.useState(null);
  const [reembedding, setReembedding] = React.useState(false);
  const [purging, setPurging] = React.useState(false);

  const fetchEmbStatus = React.useCallback(async () => {
    try {
      const res = await authedFetch(`${PIPELINE_API}/api/rag/cdm/maintenance/embedding-status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (res.ok) setEmbStatus(await res.json());
    } catch (e) { /* silent */ }
  }, []);

  React.useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await authedFetch(`${PIPELINE_API}/api/settings/processing-providers`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (res.ok) {
          const data = await res.json();
          setSettings(data.settings);
          setAvailableProviders(data.available_providers || {});
          setSubservices(data.tensornex_subservices || {});
        }
      } catch (e) { /* silent */ }
    };
    fetchSettings();
    fetchEmbStatus();
  }, [fetchEmbStatus]);

  const saveSettings = async () => {
    setSaving(true);
    setSaveError(null);
    setCacheCleared(false);
    try {
      const res = await authedFetch(`${PIPELINE_API}/api/settings/processing-providers`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(settings),
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(data.settings);
        fetchEmbStatus();
        setCacheCleared(!!data.cache_cleared);
      } else {
        let msg = 'Failed to save settings.';
        try { const err = await res.json(); msg = err.detail || msg; } catch (e) { /* ignore */ }
        setSaveError(msg);
      }
    } catch (e) { setSaveError('Network error while saving settings.'); }
    setSaving(false);
  };

  const handleReembed = async () => {
    setReembedding(true);
    try {
      const res = await authedFetch(`${PIPELINE_API}/api/rag/cdm/maintenance/reembed`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) { setReembedding(false); return; }
      const poll = setInterval(async () => {
        try {
          const sres = await authedFetch(`${PIPELINE_API}/api/rag/cdm/maintenance/embedding-status`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
          if (sres.ok) {
            const data = await sres.json();
            setEmbStatus(data);
            const js = data.reembed_status?.status;
            if (js === 'completed' || js === 'failed' || (!data.reembed_in_progress && js)) {
              clearInterval(poll);
              setReembedding(false);
            }
          }
        } catch (e) { /* keep polling */ }
      }, 2000);
    } catch (e) { setReembedding(false); }
  };

  // Purge orphaned embedding records (debris for chunks that no longer exist)
  const handlePurgeOrphans = async () => {
    setPurging(true);
    try {
      const res = await authedFetch(`${PIPELINE_API}/api/rag/cdm/maintenance/purge-orphan-embeddings`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (res.ok) await fetchEmbStatus();
    } catch (e) { /* silent */ }
    setPurging(false);
  };

  // ─── Phase 4: Blue/Green zero-downtime migration ───
  const [migStatus, setMigStatus] = React.useState(null);
  const [migrating, setMigrating] = React.useState(false);
  const MIG_RUNNING = ['provisioning', 'embedding', 'validating', 'swapping'];

  const fetchMigStatus = React.useCallback(async () => {
    try {
      const res = await authedFetch(`${PIPELINE_API}/api/rag/cdm/maintenance/migrate/status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (res.ok) setMigStatus(await res.json());
    } catch (e) { /* silent */ }
  }, []);

  React.useEffect(() => { fetchMigStatus(); }, [fetchMigStatus]);

  const handleMigrate = async () => {
    setMigrating(true);
    try {
      const res = await authedFetch(`${PIPELINE_API}/api/rag/cdm/maintenance/migrate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) { setMigrating(false); return; }
      const poll = setInterval(async () => {
        try {
          const sres = await authedFetch(`${PIPELINE_API}/api/rag/cdm/maintenance/migrate/status`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
          if (sres.ok) {
            const data = await sres.json();
            setMigStatus(data);
            if (!MIG_RUNNING.includes(data.phase)) {
              clearInterval(poll);
              setMigrating(false);
              fetchEmbStatus();
            }
          }
        } catch (e) { /* keep polling */ }
      }, 2000);
    } catch (e) { setMigrating(false); }
  };

  const handleMigAction = async (action) => {
    setMigrating(true);
    try {
      await authedFetch(`${PIPELINE_API}/api/rag/cdm/maintenance/migrate/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      await fetchMigStatus();
      await fetchEmbStatus();
    } catch (e) { /* silent */ }
    setMigrating(false);
  };

  const LABELS = {
    url_processing: { label: 'URL / Website Scraping', icon: '🌐', desc: 'How web pages are scraped and converted to text', thresholds: ['timeout_seconds'] },
    document_processing_pdf: { label: 'PDF Documents', icon: '📄', desc: 'How PDF files are parsed', thresholds: ['size_threshold_kb', 'page_threshold', 'timeout_seconds'] },
    document_processing_docx: { label: 'Word Documents (.docx)', icon: '📝', desc: 'How DOCX files are parsed', thresholds: ['size_threshold_kb', 'page_threshold', 'timeout_seconds'] },
    document_processing_xlsx: { label: 'Excel Spreadsheets (.xlsx)', icon: '📊', desc: 'How XLSX files are parsed', thresholds: ['size_threshold_kb', 'timeout_seconds'] },
    document_processing_pptx: { label: 'PowerPoint (.pptx)', icon: '📽️', desc: 'How PPTX files are parsed', thresholds: ['size_threshold_kb', 'page_threshold', 'timeout_seconds'] },
    image_processing: { label: 'Image Processing (OCR/VLM)', icon: '🖼️', desc: 'How images are processed for text extraction', thresholds: ['size_threshold_kb', 'timeout_seconds'] },
    transcription: { label: 'Audio / Video Transcription', icon: '🎙️', desc: 'How audio and video files are transcribed to text', thresholds: ['size_threshold_mb', 'timeout_seconds'] },
    embedding: { label: 'Embeddings (Vector Generation)', icon: '🔢', desc: 'How text is converted to searchable vectors. A parallel MiniLM (384-dim) copy is always kept for fast search.', thresholds: ['timeout_seconds'] },
    reranking: { label: 'Reranking (Search Quality)', icon: '🎯', desc: 'How search results are re-scored for relevance' },
  };

  const THRESHOLD_LABELS = {
    size_threshold_kb: { label: 'Max size for primary (KB)', placeholder: '10000' },
    page_threshold: { label: 'Max pages for primary', placeholder: '50' },
    size_threshold_mb: { label: 'Max size for primary (MB)', placeholder: '25' },
    timeout_seconds: { label: 'Timeout (seconds)', placeholder: '60' },
  };

  const PROVIDER_NAMES = {
    firecrawl: 'Firecrawl', reducto: 'Reducto', cohere: 'Cohere',
    openai_whisper: 'OpenAI Whisper', tensornex: 'Tensornex',
  };

  // Friendly labels for TensorNex (DocuLing) sub-services. Options come from backend.
  const SUBSERVICE_NAMES = {
    docs_chunk: 'DocuLing Parse + Chunk',
    docs_convert: 'DocuLing Convert (Markdown)',
    docs_convert_vlm: 'DocuLing VLM (Vision)',
    docs_convert_ocr: 'DocuLing OCR',
    docs_transcribe: 'DocuLing Transcribe',
    docs_transcribe_diarized: 'DocuLing Transcribe + Diarization',
    bge_m3: 'BGE-M3 (1024-dim)',
    minilm: 'MiniLM (384-dim, fast)',
  };

  if (!settings) return <div className="text-sm text-gray-400 py-4 text-center">Loading...</div>;

  return (
    <div className="space-y-3 mt-2" data-testid="pipeline-config-card">
      <p className="text-xs text-gray-500">Configure which provider handles each processing step. Primary is tried first; fallback activates on failure.</p>
      {Object.entries(LABELS).map(([key, { label, icon, desc, thresholds }]) => {
        const cat = settings[key] || { primary: 'tensornex', fallback: 'tensornex' };
        const choices = availableProviders[key] || ['tensornex'];
        return (
          <div key={key} className="py-2 border-b border-gray-100 last:border-0">
            <div className="flex items-center gap-3">
              <span className="text-lg w-7">{icon}</span>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-gray-800">{label}</div>
                <div className="text-[10px] text-gray-400">{desc}</div>
              </div>
              <div className="flex items-center gap-2">
                <div>
                  <label className="text-[10px] text-gray-400 block">Primary</label>
                  <select className="border border-gray-200 text-xs rounded px-2 py-1 w-28"
                    data-testid={`provider-primary-${key}`}
                    value={cat.primary}
                    onChange={e => setSettings(prev => ({ ...prev, [key]: { ...cat, primary: e.target.value } }))}>
                    {choices.map(p => <option key={p} value={p}>{PROVIDER_NAMES[p] || p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-gray-400 block">Fallback</label>
                  <select className="border border-gray-200 text-xs rounded px-2 py-1 w-28"
                    data-testid={`provider-fallback-${key}`}
                    value={cat.fallback}
                    onChange={e => setSettings(prev => ({ ...prev, [key]: { ...cat, fallback: e.target.value } }))}>
                    {choices.map(p => <option key={p} value={p}>{PROVIDER_NAMES[p] || p}</option>)}
                  </select>
                </div>
              </div>
            </div>
            {thresholds && thresholds.length > 0 && (
              <div className="flex items-center gap-3 mt-1.5 ml-10">
                {thresholds.map(field => (
                  <div key={field}>
                    <label className="text-[9px] text-gray-400 block">{THRESHOLD_LABELS[field]?.label || field}</label>
                    <input
                      type="number"
                      min="0"
                      className="border border-gray-200 text-[10px] rounded px-1.5 py-0.5 w-20"
                      data-testid={`threshold-${key}-${field}`}
                      placeholder={THRESHOLD_LABELS[field]?.placeholder || '0'}
                      value={cat[field] || ''}
                      onChange={e => {
                        const val = e.target.value === '' ? undefined : Number(e.target.value);
                        setSettings(prev => ({ ...prev, [key]: { ...cat, [field]: val } }));
                      }}
                    />
                  </div>
                ))}
                <div className="text-[9px] text-gray-400 mt-2.5">Exceeds size/pages, or primary times out → uses fallback</div>
              </div>
            )}
            {subservices[key] && subservices[key].length > 0 && (cat.primary === 'tensornex' || cat.fallback === 'tensornex') && (
              <div className="flex items-center gap-2 mt-1.5 ml-10" data-testid={`tensornex-subservice-row-${key}`}>
                <label className="text-[9px] text-gray-400">TensorNex service</label>
                <select
                  className="border border-gray-200 text-[10px] rounded px-1.5 py-0.5"
                  data-testid={`tensornex-service-${key}`}
                  value={cat.tensornex_service || subservices[key][0]}
                  onChange={e => setSettings(prev => ({ ...prev, [key]: { ...cat, tensornex_service: e.target.value } }))}>
                  {subservices[key].map(s => <option key={s} value={s}>{SUBSERVICE_NAMES[s] || s}</option>)}
                </select>
                <span className="text-[9px] text-gray-400">Used when TensorNex runs for this step</span>
              </div>
            )}
          </div>
        );
      })}
      {(settings.embedding?.primary !== 'tensornex' || settings.embedding?.tensornex_service === 'minilm') && (
        <p className="text-[10px] text-amber-600 px-1" data-testid="embedding-reindex-warning">Changing the embedding provider or model requires re-indexing all documents.</p>
      )}
      {embStatus && embStatus.stale_chunks > 0 && (
        <div className="rounded border border-amber-300 bg-amber-50 p-2.5 text-[11px]" data-testid="embedding-stale-banner">
          <div className="flex items-center justify-between gap-3">
            <div className="text-amber-800">
              <span className="font-semibold" data-testid="stale-chunk-count">{embStatus.stale_chunks}</span> of {embStatus.total_chunks} chunks are on a different model than the active one
              (<span className="font-medium" data-testid="active-model-name">{embStatus.active_model}</span>). Search will be unreliable until they are re-embedded.
              {embStatus.reembed_status?.status && (
                <div className="text-[10px] text-amber-700 mt-1" data-testid="reembed-progress">
                  Re-embed: {embStatus.reembed_status.status} — {embStatus.reembed_status.processed || 0}/{embStatus.reembed_status.total || 0} docs{embStatus.reembed_status.errors ? `, ${embStatus.reembed_status.errors} errors` : ''}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <button onClick={handleReembed} disabled={reembedding || embStatus.reembed_in_progress || migrating}
                className="text-[11px] px-2.5 py-1 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white rounded whitespace-nowrap"
                data-testid="reembed-btn">
                {reembedding || embStatus.reembed_in_progress ? 'Re-embedding…' : 'Re-embed all'}
              </button>
              <button onClick={handleMigrate} disabled={migrating || (migStatus && MIG_RUNNING.includes(migStatus.phase))}
                className="text-[11px] px-2.5 py-1 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded whitespace-nowrap"
                data-testid="migrate-btn"
                title="Zero-downtime: builds new vectors in parallel and swaps atomically. Search stays live.">
                {migrating || (migStatus && MIG_RUNNING.includes(migStatus.phase)) ? 'Migrating…' : 'Migrate (zero-downtime)'}
              </button>
            </div>
          </div>
        </div>
      )}
      {embStatus && embStatus.stale_chunks === 0 && embStatus.total_chunks > 0 && (
        <p className="text-[10px] text-emerald-600 px-1" data-testid="embedding-consistent-msg">All {embStatus.total_chunks} chunks use the active model ({embStatus.active_model}). ✓</p>
      )}
      {embStatus && embStatus.orphaned_records > 0 && (
        <div className="flex items-center justify-between gap-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-[11px]" data-testid="orphan-embeddings-notice">
          <span className="text-gray-600">
            <span className="font-semibold" data-testid="orphan-count">{embStatus.orphaned_records}</span> leftover embedding record{embStatus.orphaned_records === 1 ? '' : 's'} for deleted chunks (harmless debris — can't be re-embedded).
          </span>
          <button onClick={handlePurgeOrphans} disabled={purging}
            className="shrink-0 px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
            data-testid="purge-orphans-btn">
            {purging ? 'Cleaning…' : 'Clean up'}
          </button>
        </div>
      )}
      {migStatus && migStatus.phase && migStatus.phase !== 'idle' && (
        <div className="rounded border border-indigo-200 bg-indigo-50 p-2.5 text-[11px]" data-testid="migration-panel">
          <div className="flex items-center justify-between gap-3">
            <div className="text-indigo-800">
              <span className="font-medium">Blue/Green migration</span> — phase: <span className="font-semibold" data-testid="migrate-phase">{migStatus.phase}</span>
              {MIG_RUNNING.includes(migStatus.phase) && (
                <span data-testid="migrate-progress"> ({migStatus.processed || 0}/{migStatus.total || 0} docs)</span>
              )}
              {migStatus.target_model && <span className="text-indigo-600"> → {migStatus.target_model}</span>}
              {migStatus.errors ? <span className="text-red-600"> · {migStatus.errors} errors</span> : null}
              {migStatus.error && <div className="text-[10px] text-red-600 mt-1">{migStatus.error}</div>}
            </div>
            {migStatus.can_rollback && (
              <div className="flex flex-col gap-1.5">
                <button onClick={() => handleMigAction('finalize')} disabled={migrating}
                  className="text-[11px] px-2.5 py-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded whitespace-nowrap"
                  data-testid="migrate-finalize-btn"
                  title="Drop the old (blue) vectors and end the rollback window.">Finalize</button>
                <button onClick={() => handleMigAction('rollback')} disabled={migrating}
                  className="text-[11px] px-2.5 py-1 bg-gray-500 hover:bg-gray-400 disabled:opacity-50 text-white rounded whitespace-nowrap"
                  data-testid="migrate-rollback-btn"
                  title="Revert to the previous (blue) vectors.">Rollback</button>
              </div>
            )}
            {!migStatus.can_rollback && !MIG_RUNNING.includes(migStatus.phase) && (
              <button onClick={handleMigrate} disabled={migrating}
                className="text-[11px] px-2.5 py-1 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded whitespace-nowrap"
                data-testid="migrate-rerun-btn"
                title="Run a zero-downtime migration to the active embedding model.">
                {migrating ? 'Migrating…' : 'Re-run migration'}
              </button>
            )}
          </div>
        </div>
      )}
      {/* Level 7: Provider Health Status */}
      <ProviderHealthPanel />
      {saveError && (
        <p className="text-[11px] text-red-600 px-1 text-right" data-testid="pipeline-save-error">{saveError}</p>
      )}
      {cacheCleared && (
        <p className="text-[11px] text-emerald-600 px-1 text-right" data-testid="cache-cleared-note">Embedding model changed — response cache cleared so search results stay accurate.</p>
      )}
      <div className="flex justify-end pt-2">
        <button onClick={saveSettings} disabled={saving}
          className="text-xs px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded"
          data-testid="save-pipeline-config-btn">
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>
    </div>
  );
}
